import type {
  Proposal,
  ProposalBatch,
} from "@stockline/shared/types/ai/proposal";
import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { PartnerResponse } from "@/app/api/types/partner";
import type { ProductResponse } from "@/app/api/types/product";
import type { UnitResponse } from "@/app/api/types/unit";
import type {
  FlowProposalAnnotation,
  ProposalPreOrder,
} from "../types/proposal.types";

/**
 * Safe stringifier for proposal payload diff values: returns `undefined` for
 * null/undefined (so the cell renders blank), JSON-stringifies objects, and
 * falls back to `.toString()` for other primitives. Avoids the
 * `[object Object]` smell `String(value)` produces on plain objects.
 */
function stringifyDiffValue(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (
    typeof v === "number" ||
    typeof v === "boolean" ||
    typeof v === "bigint"
  ) {
    return v.toString();
  }
  return JSON.stringify(v);
}

/**
 * Coerce a proposal-payload numeric field to a JS number. The proposal payload
 * is `Record<string, unknown>` (Pydantic Decimal fields land here as JSON
 * strings like `"1.5"`), but downstream recap math (`a * b`, `acc + x`,
 * `.toFixed`) assumes real numbers. A `as number` cast satisfies the type
 * checker but doesn't coerce — concatenation creeps in and `.toFixed` then
 * throws TypeError (caught by the recap error boundary → 500 page).
 */
export function toNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Same coercion, defaulting to `0` for fields that must be numeric. */
function toNumOrZero(v: unknown, fallback: number): number {
  return toNum(v) ?? fallback;
}

/**
 * Flow fields the proposal payload may deliver as strings (Pydantic Decimal
 * serialisation). Anywhere a proposal's `change`/`add` line is merged into
 * a flow row, callers should coerce these keys via `toNum`.
 */
export const NUMERIC_FLOW_FIELDS: ReadonlySet<string> = new Set([
  "quantity",
  "weight",
  "price",
  "price_per_weight",
]);

export function buildVirtualFlow(
  proposal: Proposal,
  preOrderId: string,
  index: number,
  productMap: Map<string, ProductResponse>,
  availableUnits: UnitResponse[],
): PreOrderFlow {
  const p = proposal.payload;
  const productId = (p.product_id as string) ?? null;
  const product = productId ? (productMap.get(productId) ?? null) : null;
  const unitId = (p.unit_id as string) ?? null;
  // Hydrate `unit` from availableUnits so consumer cells see the same shape
  // as real flows (where the API populates `flow.unit`). Linear scan is fine
  // — units list is single-digit per company.
  const unit = unitId
    ? (availableUnits.find((u) => u.id === unitId) ?? null)
    : null;

  return {
    id: `proposal-flow-${proposal.id}`,
    pre_order_id: preOrderId,
    product_id: productId,
    partner_id: (p.partner_id as string) ?? null,
    product_code: (p.product_code as string) ?? product?.code ?? null,
    quantity: toNum(p.quantity),
    weight: toNum(p.weight),
    unit_id: unitId,
    unit: unit as PreOrderFlow["unit"],
    price_per_weight: toNum(p.price ?? p.price_per_weight),
    comment: (p.comment as string) ?? null,
    hidden_comment: (p.hidden_comment as string) ?? null,
    status: 0,
    index,
    product: product as PreOrderFlow["product"],
    created_at: proposal.createdAt,
    updated_at: proposal.updatedAt,
    __proposalDiff: {
      operation: "create",
      fieldDiffs: {},
      sourceProposalId: proposal.id,
    },
  } as PreOrderFlow;
}

export function buildVirtualPreOrder(
  batch: ProposalBatch,
  preOrderProposal: Proposal,
  flowProposals: Proposal[],
  partnerMap: Map<string, PartnerResponse>,
  productMap: Map<string, ProductResponse>,
  availableUnits: UnitResponse[],
): ProposalPreOrder {
  const p = preOrderProposal.payload;
  const virtualId = `proposal-${batch.id}`;
  const partnerId = (p.partner_id as string) ?? undefined;
  const partner = partnerId ? (partnerMap.get(partnerId) ?? null) : null;

  const flows = flowProposals.map((fp, i) =>
    buildVirtualFlow(fp, virtualId, i, productMap, availableUnits),
  );

  return {
    id: virtualId,
    user_id: batch.userId,
    type: toNumOrZero(p.type, 0),
    status: 0,
    source_type: toNumOrZero(p.source_type, 3),
    partner_id: partnerId,
    partner: partner as ProposalPreOrder["partner"],
    employee_id: (p.employee_id as string) ?? null,
    delivery_date: (p.delivery_date as string) ?? null,
    order_date: (p.order_date as string) ?? null,
    comment: (p.comment as string) ?? null,
    pre_order_number: null,
    customer_po_number: null,
    created_at: batch.createdAt,
    updated_at: batch.updatedAt,
    flows,
    pre_order_metadata: { seen: false, modified_by_ai: true },
    __proposal: {
      batchId: batch.id,
      operation: "create",
      batch,
      proposals: batch.proposals,
      sourceProposalId: preOrderProposal.id,
    },
  } as ProposalPreOrder;
}

export function buildFlowDiffs(
  proposal: Proposal,
): Record<string, { oldValue?: string; newValue?: string }> {
  const diffs: Record<string, { oldValue?: string; newValue?: string }> = {};
  const displayFields = proposal.payload?.display_fields;

  if (Array.isArray(displayFields)) {
    for (const field of displayFields as Array<{
      label: string;
      value: string;
      oldValue?: string;
    }>) {
      diffs[field.label] = {
        newValue: field.value,
        oldValue: field.oldValue,
      };
    }
  }

  return diffs;
}

export function extractLinesToChangeAnnotations(
  proposal: Proposal,
  flowAnnotations: Map<string, FlowProposalAnnotation>,
): void {
  const linesToChange = proposal.payload?.lines_to_change as
    | Array<Record<string, unknown>>
    | undefined;
  if (!Array.isArray(linesToChange)) return;

  for (const line of linesToChange) {
    const flowId = line.id as string | undefined;
    if (!flowId) continue;
    const fieldDiffs: Record<string, { oldValue?: string; newValue?: string }> =
      {};
    for (const [key, value] of Object.entries(line)) {
      if (key === "id") continue;
      fieldDiffs[key] = { newValue: stringifyDiffValue(value) };
    }
    flowAnnotations.set(flowId, {
      operation: "update",
      fieldDiffs,
      sourceProposalId: proposal.id,
    });
  }
}
